import React from 'react';
import SortTable from '../SortTable';
import ListResults from '../ListResults';
import shallowequal from 'shallowequal';
import {Link} from 'react-router';
import filter from "../../filter";
import { executeBackendQuery } from "../../requests/executeBackendQuery";
import { manageData } from "../../requests/CRUD";
import { INVESTOR_LIST, INVESTOR_LIST_COUNT, INVESTOR_HOT_LIST_LIST, INVESTOR_HOT_LIST_COUNT } from '../../consts/queries';
import { INVESTOR_HOT_LIST } from '../../consts/tables';
import LinkComp from "../LinkComp";
import FilterButton from '../FilterButton';
import FindDuplicateContacts from '../FindDuplicateContacts';
import { hasFilter } from '../FindDuplicateContacts/fetchFilteredIds';
import OpenInCrmButton from "../OpenInCrmButton/OpenInCrmButton";
import DeceasedPill from "../DeceasedPill/DeceasedPill";
import Icon from '../Icon';
import plus from '../../svgs/plus-white.svg';
import {buildServerQuery, applyClientSort, applySort} from "../../sort-utils";
import {formatPhoneNumber} from "../../utils";

const limit = 50;
const filteredCountLimit = 10000;
// Rows per request when Find Dups reads the whole filtered list. Asking for
// all of it at once exhausts the server's memory limit, which answers a bare
// 500 with nothing in the log (see docs/PROJECT-CONTEXT.md).
const pageSize = 500;

function isInvestorDeleted(item) {
	if (!item) return true;
	const deleted = item.DELETED;
	return deleted === '1' || deleted === 1 || (deleted != null && deleted !== '' && deleted !== '0');
}

function phoneDigitsMatch(searchValue, ...phoneFields) {
	const digits = String(searchValue).replace(/\D/g, '');
	if (!digits) return true;
	return phoneFields.some((field) => String(field || '').replace(/\D/g, '').includes(digits));
}

function hasInvestorFilters(query) {
	return !!(query.NAME || query.EMAIL || query.PHONE || query.CELL || query.LEAD_ID || query.FIRM_ID);
}

function applyInvestorClientFilters(items, query) {
	if (!items || !Array.isArray(items)) return [];
	return items.filter((item) => {
		if (!item) return false;
		if (isInvestorDeleted(item)) return false;
		if (query.NAME) {
			const term = String(query.NAME).trim().toLowerCase();
			// A deceased investor is found by their 2nd Contact's name too.
			const names = [item.NAME, item.SECOND_CONTACT_NAME].filter(Boolean).map((name) => String(name).toLowerCase());
			if (!names.some((name) => name.includes(term))) return false;
		}
		if (query.EMAIL) {
			const term = String(query.EMAIL).trim().toLowerCase();
			if (!item.EMAIL || !String(item.EMAIL).toLowerCase().includes(term)) return false;
		}
		if (query.PHONE && !phoneDigitsMatch(query.PHONE, item.PHONE, item.CELL)) return false;
		if (query.CELL && !phoneDigitsMatch(query.CELL, item.CELL)) return false;
		if (query.LEAD_ID) {
			const term = String(query.LEAD_ID).trim();
			if (!item.LEAD_ID || !String(item.LEAD_ID).includes(term)) return false;
		}
		if (query.FIRM_ID) {
			if (String(item.FIRM_ID) !== String(query.FIRM_ID)) return false;
		}
		return true;
	});
}

export default class InvestorList extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			items: null,
			totalCount: null,
			error: null,
			isBusy: null,
			removingItems: new Set(),
			findDupsOpen: false,
		};
		this.handleHotListRemove = this.handleHotListRemove.bind(this);
		this.fetchFilteredIdsForDups = this.fetchFilteredIdsForDups.bind(this);
	}

	/**
	 * The ids of every investor the current filter matches, for Find Dups.
	 *
	 * Not the shared helper: this screen narrows by phone and name in the
	 * browser (a server LIKE cannot match "(832) 678-3590" against
	 * "8326783590"), so the same client-side pass has to run here or the
	 * popup would compare rows the list is not showing.
	 */
	fetchFilteredIdsForDups() {
		const query = {...this.props.location.query};

		// Unfiltered, the list is every investor — ~108,000 rows. Walking
		// them into the browser to hand the ids straight back is slow and
		// would hit the cap, so the server scans the table itself.
		if (!hasFilter(query)) return Promise.resolve(null);

		const serverQuery = {...query};
		delete serverQuery.PHONE;
		delete serverQuery.CELL;

		const options = filter(buildServerQuery(serverQuery), 'investors');
		const collected = [];

		const fetchPage = (offset) => {
			const pageOptions = {...options, param: {...options.param, limit: pageSize, offset}};

			return executeBackendQuery({queryName: INVESTOR_LIST, options: pageOptions}).then((res) => {
				if (!res.success) throw new Error('This list could not be loaded.');

				const rows = Array.isArray(res.data) ? res.data : [];
				applyInvestorClientFilters(rows, query).forEach((row) => {
					const id = Number(row.ARCIMEDES_ROW_ID);
					if (id) collected.push(id);
				});

				if (collected.length >= filteredCountLimit) {
					throw new Error(
						`This filter matches more than ${filteredCountLimit.toLocaleString()} investors. `
						+ 'Narrow it before searching for duplicates.',
					);
				}

				if (rows.length < pageSize) {
					return collected;
				}

				return fetchPage(offset + pageSize);
			});
		};

		return fetchPage(0);
	}

	componentDidMount() {
		this.fetch(this.props.location.query);
	}

	isHotListMode(query) {
		return query && query.HOT_LIST === 'y';
	}

	fetch(q) {
		this.setState({isBusy: true});

		if (this.isHotListMode(q)) {
			this.fetchHotList(q);
			return;
		}

        const query = {...q};
		const filtered = hasInvestorFilters(query);
		const pageNum = query.page ? Number(query.page) : 1;

		if (filtered) {
			// Phone matching is digit-normalized client-side; server LIKE cannot match
			// formatted numbers like "(832) 678-3590" against "8326783590".
			const serverQuery = {...query};
			if (serverQuery.PHONE) {
				delete serverQuery.PHONE;
			}
			if (serverQuery.CELL) {
				delete serverQuery.CELL;
			}
			const options = filter(buildServerQuery(serverQuery), 'investors');
			const fetchOptions = {
				...options,
				param: {...options.param, limit: filteredCountLimit, offset: 0},
			};
			executeBackendQuery({queryName: INVESTOR_LIST, options: fetchOptions}).then((listRes) => {
				const allItems = listRes.success && Array.isArray(listRes.data)
					? applyInvestorClientFilters(listRes.data, query)
					: [];
				const sorted = applySort(allItems, query.order);
				const start = (pageNum - 1) * limit;
				this.setState({
					items: sorted.slice(start, start + limit),
					totalCount: sorted.length,
					error: listRes.success ? null : (listRes.message || 'Unknown error occurred'),
					isBusy: null,
				});
			}).catch((err) => {
				this.setState({ items: [], totalCount: false, error: err.message || 'Failed to fetch investors', isBusy: null });
			});
			return;
		}

		const options = filter(buildServerQuery(query), 'investors');
        options.param.limit = limit;
        options.param.offset = (pageNum - 1) * limit;

		Promise.all([
			executeBackendQuery({queryName: INVESTOR_LIST, options: options}),
			executeBackendQuery({queryName: INVESTOR_LIST_COUNT, options: options}),
		]).then(res => {
			const listRes = res[0];
			const countRes = res[1];
			const totalCount = countRes.success && countRes.data && countRes.data[0]
				? Number(countRes.data[0].TOTAL_COUNT)
				: false;
			this.setState({
				items: listRes.success && Array.isArray(listRes.data) ? applyClientSort(listRes.data, query.order) : [],
				totalCount,
				error: listRes.success ? null : (listRes.message || 'Unknown error occurred'),
				isBusy: null,
			});
		}).catch(err => {
			this.setState({ items: [], totalCount: false, error: err.message || 'Failed to fetch investors', isBusy: null });
		});
	}

	fetchHotList(q) {
		const userEmail = this.props.userData && this.props.userData.email;
		if (!userEmail) {
			this.setState({ items: [], totalCount: 0, error: null, isBusy: null });
			return;
		}
		const pageNum = q.page ? Number(q.page) : 1;
		
		Promise.all([
			executeQuery({
				queryName: INVESTOR_HOT_LIST_LIST,
				options: { param: { USER_EMAIL: userEmail, limit: limit, offset: (pageNum - 1) * limit } },
				expireTime: 0,
			}),
			executeQuery({
				queryName: INVESTOR_HOT_LIST_COUNT,
				options: { param: { USER_EMAIL: userEmail } },
				expireTime: 0,
			}),
		]).then(([listRes, countRes]) => {
			const items = listRes.success && Array.isArray(listRes.data) ? listRes.data : [];
			const totalCount = countRes.success && countRes.data && countRes.data[0]
				? Number(countRes.data[0].TOTAL_COUNT)
				: items.length;
			
			this.setState({
				items,
				totalCount,
				error: listRes.success ? null : (listRes.message || 'Failed to fetch investor hot list'),
				isBusy: null,
			});
		}).catch((err) => {
			console.error('[InvestorList] Hot list fetch failed', err);
			this.setState({ items: [], totalCount: false, error: err.message || 'Failed to fetch investor hot list', isBusy: null });
		});
	}

	fetchHotList(q) {
		const userEmail = this.props.userData && this.props.userData.email;
		if (!userEmail) {
			this.setState({ items: [], totalCount: 0, error: null, isBusy: null });
			return;
		}
		const pageNum = q.page ? Number(q.page) : 1;
		
		Promise.all([
			executeBackendQuery({
				queryName: INVESTOR_HOT_LIST_LIST,
				options: { param: { USER_EMAIL: userEmail, limit: limit, offset: (pageNum - 1) * limit } },
			}),
			executeBackendQuery({
				queryName: INVESTOR_HOT_LIST_COUNT,
				options: { param: { USER_EMAIL: userEmail } },
			}),
		]).then(([listRes, countRes]) => {
			const items = listRes.success && Array.isArray(listRes.data) ? listRes.data : [];
			const totalCount = countRes.success && countRes.data && countRes.data[0]
				? Number(countRes.data[0].TOTAL_COUNT)
				: items.length;
			
			this.setState({
				items,
				totalCount,
				error: listRes.success ? null : (listRes.message || 'Failed to fetch investor hot list'),
				isBusy: null,
			});
		}).catch((err) => {
			console.error('[InvestorList] Hot list fetch failed', err);
			this.setState({ items: [], totalCount: false, error: err.message || 'Failed to fetch investor hot list', isBusy: null });
		});
	}

	handleHotListRemove(item) {
		const hotListRowId = item.HOT_LIST_ROW_ID;
		const investorId = item.ARCIMEDES_ROW_ID;
		const userEmail = this.props.userData && this.props.userData.email;

		if (!hotListRowId) return;
		
		if (this.state.removingItems.has(hotListRowId)) return;

		this.setState((prevState) => ({
			removingItems: new Set(prevState.removingItems).add(hotListRowId),
			items: (prevState.items || []).filter((i) => i.HOT_LIST_ROW_ID !== hotListRowId),
		}));

		manageData({
			tableCode: INVESTOR_HOT_LIST,
			data: [{ ARCIMEDES_ROW_ID: hotListRowId, DELETED: 1 }],
		}).catch((err) => {
			console.warn('[investor-hotlist] Arc remove failed', err);
		});

		if (investorId && userEmail) {
			fetch('/api/crm/investor-hotlist', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ investorId, userEmail, action: 'remove' }),
			}).catch((err) => {
				console.warn('[investor-hotlist] CRM remove sync failed (non-fatal)', err);
			});
		}
	}

	componentDidUpdate(prevProps) {
		if (!shallowequal(prevProps.location.query, this.props.location.query)) {
			this.fetch(this.props.location.query);
		}
	}

	render() {
		const query = this.props.location.query;
		const isHotListMode = this.isHotListMode(query);

		const baseColumns = [
			// Edit column temporarily disabled
			// {
			// 	name: 'Edit',
			// 	columnStyle: {width: 70},
			// 	render: (el) => (
			// 		<Link to={{pathname: `${arc.path.base}investor/${el.ARCIMEDES_ROW_ID}`}}><Icon svg={svgEdit}/></Link>
			// 	),
			// },
			{
				code: 'NAME',
				name: 'Name',
				columnStyle: { whiteSpace: 'nowrap' },
				render: (el) => (
					<React.Fragment>
						<Link to={{pathname: `${arc.path.base}investor/${el.ARCIMEDES_ROW_ID}`}}>{el.NAME}</Link>
						<DeceasedPill investor={el} />
						{el.SECOND_CONTACT_NAME && (
							<div className="InvestorList-second-contact">
								2nd Contact: <Link to={{pathname: `${arc.path.base}investor/${el.SECOND_CONTACT_ID}`}}>{el.SECOND_CONTACT_NAME}</Link>
							</div>
						)}
					</React.Fragment>
				),
				headerStyle: { minWidth: 218 },
			}, {
				code: 'EMAIL',
				name: 'Email',
				columnStyle: { whiteSpace: 'nowrap' },
				render: (el) => el.EMAIL ? <a href={`mailto:${el.EMAIL}`}>{el.EMAIL}</a> : null,
			}, {
				code: 'CELL',
				name: 'Cell',
				columnStyle: { minWidth: 132, whiteSpace: 'nowrap' },
				render: (el) => el.CELL ?
					<a href={`tel:${el.CELL.replace(/\D/g, '')}`}>{formatPhoneNumber(el.CELL)}</a> : null,
			}, {
				code: 'PHONE',
				name: 'Work',
				columnStyle: { minWidth: 132, whiteSpace: 'nowrap' },
				render: (el) => el.PHONE ?
					<a href={`tel:${el.PHONE.replace(/\D/g, '')}`}>{formatPhoneNumber(el.PHONE)}</a> : null,
			}, {
				code: 'ADDRESS',
				name: 'Address',
				columnStyle: { whiteSpace: 'nowrap' },
				render: (el) => el.ADDRESS,
			}, {
				code: 'CITY',
				name: 'City',
				columnStyle: { whiteSpace: 'nowrap' },
				render: (el) => el.CITY,
			}, {
				code: 'STATE',
				name: 'State',
				columnStyle: { minWidth: 72, whiteSpace: 'nowrap' },
				render: (el) => el.STATE,
			}, {
				code: 'ZIP',
				name: 'Zip',
				columnStyle: { minWidth: 88, whiteSpace: 'nowrap' },
				render: (el) => el.ZIP,
			}, {
				code: 'LEAD_ID',
				name: 'Lead ID',
				columnStyle: { whiteSpace: 'nowrap' },
				render: (el) => (
					<OpenInCrmButton leadId={el.LEAD_ID} entityType="lead" variant="link" label="Open in CRM" />
				),
			}, {
				code: 'FIRM_NAME',
				name: 'Firm',
				columnStyle: { whiteSpace: 'nowrap' },
				render: (el) => el.FIRM_NAME,
			},
		];
        
		const columns = isHotListMode
			? [
				{
					name: '',
					columnStyle: { width: 36, textAlign: 'center' },
					render: (el) => (
						<button
							onClick={() => this.handleHotListRemove(el)}
							title="Remove from Hot List"
							style={{
								background: 'none',
								border: 'none',
								cursor: 'pointer',
								color: '#c62828',
								fontSize: '16px',
								lineHeight: 1,
								padding: '2px 4px',
							}}
						>
							★
						</button>
					),
				},
				...baseColumns,
			]
			: baseColumns;

		const pageTitle = isHotListMode ? 'Investor Hot List' : 'Investors';

		return (
			<div className={`InvestorList ${isHotListMode ? 'InvestorList--hotlist' : ''}`} data-is-busy={this.state.isBusy} style={{width: '100%'}}>
				<div className='page-rounded-corner-container'>
					<div className='page-header-container list'>
						<div className='page-header list' style={{flexDirection: 'column', alignItems: 'flex-start'}}>
							<div style={{display: 'flex', alignItems: 'center', gap: '10px', width: '100%'}}>
								<div className='page-title'>{pageTitle}</div>
								{isHotListMode && (
									<Link
										to={`${arc.path.base}investors`}
										style={{fontSize: '13px', color: '#666', textDecoration: 'none', marginLeft: '4px'}}
									>
										← All Investors
									</Link>
								)}
								{!isHotListMode && (
									<LinkComp to={`${arc.path.base}investor`}>
										<div className="button-new plus positive">
											<Icon svg={plus}/>
										</div>
									</LinkComp>
								)}
								{/* The hot list is a hand-picked selection, not the roster to de-duplicate. */}
								{!isHotListMode && (
									<button
										type="button"
										className="button-new text"
										onClick={() => this.setState({findDupsOpen: true})}
										title="Find investors recorded more than once"
									>
										Find Dups
									</button>
								)}
							</div>
							{!isHotListMode && <FilterButton onClick={this.props.openFilter} />}
						</div>
						<div className='line'/>
					</div>
				</div>
				<FindDuplicateContacts
					open={Boolean(this.state.findDupsOpen) && !isHotListMode}
					entity="investors"
					label="Investor"
					plural="Investors"
					route="investor"
					fetchFilteredSet={this.fetchFilteredIdsForDups}
					filteredCount={this.state.totalCount}
					onClose={() => this.setState({findDupsOpen: false})}
				/>
				<div className='page-body-container'>
					<SortTable columns={columns} items={this.state.items} query={query} hasSubColumns={false} classNameModifier='investors'/>
					<ListResults query={this.props.location.query} pathname={this.props.location.pathname} error={this.state.error} totalCount={this.state.totalCount} itemCount={this.state.items ? this.state.items.length : false} limit={limit}/>
				</div>
			</div>
		);
	}
}
